import { useState, useEffect } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { Avatar } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { Clock, MapPin, Globe, Loader2 } from 'lucide-react';
import { detectTimeZone } from '@/lib/timezone';
import {
  getTimeSlots,
  getEventTypeById,
  rescheduleBooking,
  getBookingById,
  getUserProfile,
} from '@/client/sdk.gen';
import {
  EventTypeResponse,
  BookingResponse,
  UserResponse,
  UserProfileResponse,
  OrganisationMemberResponse,
} from '@/client/types.gen';
import { BookingConfirmation } from '@/components/booking-confirmation';
import { Button } from '@/components/ui/button';
import { SessionAuth } from 'supertokens-auth-react/recipe/session';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { DateTimeSelector } from '@/components/date-time-selector';

// Extend the BookingResponse type to include uid
interface ExtendedBookingResponse extends BookingResponse {
  uid: string;
}

export function RescheduleBookingPage() {
  const [searchParams] = useSearchParams();
  const params = useParams<Record<string, string | undefined>>();
  const navigate = useNavigate();
  const bookingId = params.bookingId;

  // Get data from search params
  const queryEventTypeId = searchParams.get('eventTypeId');
  const queryUsername = searchParams.get('username') || '';

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{
    start: string;
    end: string;
  } | null>(null);
  const [eventTypeDetails, setEventTypeDetails] =
    useState<EventTypeResponse | null>(null);
  const [finalHosts, setFinalHosts] = useState<OrganisationMemberResponse[]>(
    []
  );
  const [originalBooking, setOriginalBooking] =
    useState<ExtendedBookingResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [booking, setBooking] = useState<ExtendedBookingResponse | null>(null);
  const [timezone, setTimezone] = useState<string>(detectTimeZone());
  const [hostUsername, setHostUsername] = useState<string>('');
  const [rescheduleReason, setRescheduleReason] = useState<string>('');

  // Fetch original booking and event type details when component mounts
  useEffect(() => {
    async function fetchBookingDetails() {
      setIsLoading(true);
      setError(null);

      try {
        if (!bookingId) {
          throw new Error('Booking ID is required');
        }

        // Fetch the original booking details
        const bookingResponse = await getBookingById({
          path: { booking_id: bookingId },
        });

        if (bookingResponse.error) {
          throw new Error(`Failed to fetch booking: ${bookingResponse.error}`);
        }

        if (!bookingResponse.data) {
          throw new Error('No booking data found');
        }

        // Set original booking data
        setOriginalBooking(bookingResponse.data as ExtendedBookingResponse);

        // Get event type details using the event_type_id from the booking
        const eventTypeId = bookingResponse.data.event_type_id;

        const response = await getEventTypeById({
          path: { event_type_id: eventTypeId },
        });

        if (response.error) {
          throw new Error(`Failed to fetch event type: ${response.error}`);
        }

        if (response.data) {
          setEventTypeDetails(response.data);
          // Use event_type_hosts as fallback for hosts
          setFinalHosts(response.data.event_type_hosts || []);

          // Get current user profile to access the username
          const userProfileResponse = await getUserProfile();

          if (userProfileResponse.error) {
            console.error(
              `Failed to fetch user profile: ${userProfileResponse.error}`
            );
            // Continue without username - slots may not work correctly
          } else if (userProfileResponse.data) {
            // Set the username from the user profile
            setHostUsername(userProfileResponse.data.username);

            // Once we have the username and event type slug, we can get final_event_type_hosts
            if (response.data.slug) {
              const today = new Date();
              const timeSlotsResponse = await getTimeSlots({
                path: {
                  username: userProfileResponse.data.username,
                  event_type_slug: response.data.slug,
                },
                query: {
                  year: today.getFullYear(),
                  month: today.getMonth() + 1,
                  day: today.getDate(),
                  timezone: timezone,
                },
              });

              if (!timeSlotsResponse.error && timeSlotsResponse.data) {
                // Update with more accurate host information
                setFinalHosts(
                  timeSlotsResponse.data.final_event_type_hosts || []
                );
              }
            }
          }
        }
      } catch (err) {
        console.error('Error fetching booking details:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to load booking details'
        );
      } finally {
        setIsLoading(false);
      }
    }

    fetchBookingDetails();
  }, [bookingId, timezone]);

  const handleTimeSelected = (
    date: Date,
    startTime: string,
    endTime: string
  ) => {
    setSelectedDate(date);
    setSelectedTimeSlot({ start: startTime, end: endTime });
  };

  const handleReschedule = async () => {
    if (!selectedTimeSlot || !originalBooking) return;

    try {
      setIsLoading(true);
      const response = await rescheduleBooking({
        path: { booking_id: originalBooking.id },
        body: {
          start_time: selectedTimeSlot.start,
          end_time: selectedTimeSlot.end,
          reason: rescheduleReason || null,
        },
      });

      if (response.error) {
        throw new Error(`Failed to reschedule: ${response.error}`);
      }

      if (response.data) {
        setBooking(response.data as ExtendedBookingResponse);
        // Scroll to top of page after booking is rescheduled
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      console.error('Error rescheduling booking:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to reschedule booking'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDone = () => {
    navigate('/dashboard');
  };

  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="mx-auto h-10 w-10 text-primary animate-spin" />
            <p className="mt-4 text-muted-foreground">
              Loading booking details...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="flex-1 flex items-center justify-center">
          <div className="p-6 max-w-md rounded-lg border border-destructive/30 bg-destructive/10">
            <h2 className="text-xl font-semibold mb-4 text-destructive">
              Error
            </h2>
            <p className="text-muted-foreground">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (booking) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
          <BookingConfirmation booking={booking} onDone={handleDone} />
        </div>
      </div>
    );
  }

  if (!eventTypeDetails || !originalBooking) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="flex-1 flex items-center justify-center">
          <div className="p-6 max-w-md rounded-lg border border-destructive/30 bg-destructive/10">
            <h2 className="text-xl font-semibold mb-4 text-destructive">
              Error
            </h2>
            <p className="text-muted-foreground">No booking details found</p>
          </div>
        </div>
      </div>
    );
  }

  const isCollectiveEvent = finalHosts.length > 1;

  return (
    <div className="flex flex-col min-h-screen">
      <div className="flex-1 container mx-auto px-4 py-8 max-w-6xl">
        <header className="mb-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
            Reschedule Your Appointment
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Select a new date and time for your meeting with{' '}
            {isCollectiveEvent ? 'the team' : finalHosts[0]?.email || 'us'}.
          </p>
        </header>

        <div className="grid md:grid-cols-2 gap-8 items-start justify-center">
          {/* Left column - Event details */}
          <div className="space-y-6 max-w-md mx-auto md:mx-0 w-full">
            <Card className="overflow-hidden border shadow-md group hover:shadow-lg transition-shadow">
              <div className="p-6 border-b bg-muted">
                <h2 className="text-2xl font-bold tracking-tight">
                  {eventTypeDetails?.name}
                </h2>
                {eventTypeDetails?.description && (
                  <p className="mt-2 text-muted-foreground">
                    {eventTypeDetails.description}
                  </p>
                )}
              </div>

              <CardContent className="p-6 space-y-4">
                {finalHosts.length > 0 && (
                  <div className="pb-4 border-b border-border">
                    {isCollectiveEvent ? (
                      <div className="mb-2">
                        <p className="font-medium text-foreground">
                          Collective Event with:
                        </p>
                      </div>
                    ) : null}

                    {finalHosts.map((host) => (
                      <div
                        key={host.email}
                        className="flex items-center space-x-4 mb-3"
                      >
                        <Avatar className="h-12 w-12 border">
                          <div className="bg-primary text-primary-foreground flex items-center justify-center h-full w-full rounded-full text-xl font-semibold">
                            {host.email.charAt(0)}
                          </div>
                        </Avatar>
                        <div>
                          <p className="font-medium text-foreground">
                            {host.email}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {host.role || 'Host'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center p-3 rounded-lg bg-accent/50">
                    <Clock className="mr-3 h-5 w-5 text-primary" />
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        Duration
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {eventTypeDetails?.duration} minutes
                      </p>
                    </div>
                  </div>

                  {eventTypeDetails?.locations &&
                    eventTypeDetails.locations.length > 0 && (
                      <div className="flex items-center p-3 rounded-lg bg-accent/50">
                        <MapPin className="mr-3 h-5 w-5 text-primary" />
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            Location
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {eventTypeDetails.locations[0].type}
                          </p>
                        </div>
                      </div>
                    )}

                  <div className="flex items-center p-3 rounded-lg bg-accent/50">
                    <Globe className="mr-3 h-5 w-5 text-primary" />
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        Timezone
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {timezone}
                      </p>
                    </div>
                  </div>
                </div>

                {selectedTimeSlot && (
                  <div className="mt-6 space-y-4">
                    <div className="p-4 rounded-lg border bg-muted">
                      <h3 className="font-medium mb-2 text-foreground">
                        New Appointment Time
                      </h3>
                      <div className="flex items-center text-sm">
                        <Clock className="mr-2 h-4 w-4 text-primary" />
                        <span className="text-foreground">
                          {new Date(selectedTimeSlot.start).toLocaleString(
                            undefined,
                            {
                              dateStyle: 'full',
                              timeStyle: 'short',
                            }
                          )}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="reschedule-reason"
                        className="text-foreground"
                      >
                        Reason for rescheduling (optional)
                      </Label>
                      <Textarea
                        id="reschedule-reason"
                        placeholder="Please provide a reason for rescheduling"
                        value={rescheduleReason}
                        onChange={(e) => setRescheduleReason(e.target.value)}
                        className="resize-none"
                        rows={3}
                      />
                    </div>

                    <Button
                      className="w-full"
                      onClick={handleReschedule}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Rescheduling...
                        </>
                      ) : (
                        'Confirm Reschedule'
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right column - Calendar and time slots */}
          <DateTimeSelector
            username={hostUsername}
            eventTypeSlug={eventTypeDetails.slug}
            onTimeSelected={handleTimeSelected}
            initialTimezone={timezone}
          />
        </div>
      </div>
    </div>
  );
}

// Wrap component with SessionAuth to protect this page
export default function ProtectedRescheduleBookingPage() {
  return (
    <SessionAuth>
      <RescheduleBookingPage />
    </SessionAuth>
  );
}
